import {publicActionLabel} from '../../../../shared/agentActionLabels';
import type {CanvasChatMessage} from './canvas-types';
import {acceptsConceptProgress} from '../../../../shared/conceptProgressOrder';

export interface ConversationProjectionEvent {type: string; data?: any}
export const isConversationProjectionEvent = (type: string) => [
  'agent_task_start', 'agent_step', 'agent_step_result', 'agent_step_progress', 'agent_task_complete',
  'concept_draft_progress', 'concept_steering_state', 'angle_set_card', 'open_brand_modal',
].includes(type);

/** Live and durable streams project the same owned chat messages. Replayed
 * starts/steps are idempotent and cannot reset an already-finished step.
 */
export function projectConversationEvent(message: CanvasChatMessage, assistantId: string, event: ConversationProjectionEvent): CanvasChatMessage {
  const d = event.data ?? {};
  if (event.type === 'concept_steering_state') {
    return message.role === 'assistant' && message.id === d.sourceRunMessageId && d.conceptsRun
      ? {...message, conceptsRun: d.conceptsRun} : message;
  }
  if (message.role !== 'assistant' || message.id !== assistantId) return message;
  const groups = message.agentTaskGroups ?? [];
  switch (event.type) {
    case 'agent_task_start': {
      if (typeof d.taskGroupId !== 'string' || !d.taskGroupId || groups.some(group => group.id === d.taskGroupId)) return message;
      return {...message, agentThinking: d.label || 'Working…', conceptsRun: d.conceptsRun ?? message.conceptsRun,
        agentTaskGroups: [...groups.map(group => group.status === 'active' ? {...group, status: 'completed' as const} : group),
          {id: d.taskGroupId, label: d.label || 'Working…', status: 'active', steps: [], iterationIndex: d.iterationIndex ?? 0}]};
    }
    case 'concept_draft_progress': return acceptsConceptProgress(message.conceptDraftProgress, d) ? {...message, conceptDraftProgress: d} : message;
    case 'agent_step': return {...message, agentThinking: publicActionLabel(d.toolName,d.friendlyLabel), agentTaskGroups: groups.map(group =>
      group.id !== d.taskGroupId || group.steps.some(step => step.id === d.stepId) ? group : {...group,
        steps: [...group.steps.filter(step => step.toolName !== '__thinking__'), {id: d.stepId, toolName: d.toolName,
          friendlyLabel: publicActionLabel(d.toolName,d.friendlyLabel), status: 'running', contextDetail: d.detail || undefined}]} )};
    case 'agent_step_result': return {...message, agentTaskGroups: groups.map(group => group.id !== d.taskGroupId ? group : {...group,
      steps: group.steps.map(step => step.id !== d.stepId ? step : {...step, status: d.success ? 'success' : 'error',
        summary: d.summary, detail: d.detail, durationMs: typeof d.durationMs === 'number' ? d.durationMs : undefined})})};
    case 'agent_step_progress': return !d.stepId || !d.message ? message : {...message, agentTaskGroups: groups.map(group => ({...group,
      steps: group.steps.map(step => step.id === d.stepId ? {...step, contextDetail: d.message} : step)}))};
    case 'agent_task_complete': return {...message, conceptsRun: d.conceptsRun ?? message.conceptsRun,
      agentTaskGroups: groups.map(group => group.id === d.taskGroupId ? {...group, status: d.success ? 'completed' : 'failed'} : group)};
    case 'angle_set_card': return !Number.isSafeInteger(d.setId) || d.setId <= 0 ? message : {...message,
      angleSetCard: {setId: d.setId, title: d.title ?? 'Angle Set', angleCount: d.angleCount,
        ...(Array.isArray(d.optionIds) ? {optionIds: d.optionIds} : {})}};
    default: return message;
  }
}
